import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Tag } from 'antd';
import {
  EnvironmentOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  UserAddOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import request from '../utils/request';
import './Dashboard.css';

const Dashboard = () => {
  const [stats, setStats] = useState({
    communityCount: 0,
    userCount: 0,
    todayCheckin: 0,
    pendingAppointment: 0,
    totalCheckin: 0
  });
  const [recentCheckins, setRecentCheckins] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
    fetchRecentCheckins();
  }, []);

  const fetchStats = async () => {
    try {
      const [communityRes, userRes, checkinRes, appointmentRes] = await Promise.all([
        request.get('/community'),
        request.get('/user'),
        request.get('/checkin/stats'),
        request.get('/appointment/pending')
      ]);

      setStats({
        communityCount: communityRes.data?.total || (communityRes.data?.list || []).length,
        userCount: userRes.data?.total || (userRes.data?.list || []).length,
        todayCheckin: checkinRes.data?.today_count || 0,
        totalCheckin: checkinRes.data?.total || 0,
        pendingAppointment: (appointmentRes.data?.list || appointmentRes.data || []).length
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentCheckins = async () => {
    try {
      const response = await request.get('/checkin/records', {
        params: { page: 1, pageSize: 10 }
      });
      if (response.code === 200) {
        setRecentCheckins(response.data.list || []);
      }
    } catch (error) {
      console.error('Failed to fetch recent checkins:', error);
    }
  };

  const statCards = [
    {
      title: '小区数量',
      value: stats.communityCount,
      icon: <EnvironmentOutlined />,
      color: '#1890ff',
      bgColor: '#e6f7ff'
    },
    {
      title: '用户数量',
      value: stats.userCount,
      icon: <TeamOutlined />,
      color: '#52c41a',
      bgColor: '#f6ffed'
    },
    {
      title: '今日核销',
      value: stats.todayCheckin,
      icon: <CheckCircleOutlined />,
      color: '#faad14',
      bgColor: '#fffbe6'
    },
    {
      title: '待审核预约',
      value: stats.pendingAppointment,
      icon: <UserAddOutlined />,
      color: '#f5222d',
      bgColor: '#fff1f0'
    }
  ];

  const columns = [
    {
      title: '访客姓名',
      dataIndex: 'visitor_name',
      key: 'visitor_name',
      render: (text) => <span className="visitor-name">{text}</span>
    },
    {
      title: '访问房号',
      dataIndex: 'room_number',
      key: 'room_number',
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: '核销时间',
      dataIndex: 'checkin_time',
      key: 'checkin_time',
      render: (text) => text ? new Date(text).toLocaleString('zh-CN') : '-'
    },
    {
      title: '操作员',
      dataIndex: 'operator_name',
      key: 'operator_name',
      render: (text) => text || '系统'
    }
  ];

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>数据概览</h1>
        <p>欢迎使用物业访客管理系统</p>
      </div>

      <Row gutter={[20, 20]} className="stat-cards">
        {statCards.map((stat, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card className="stat-card" variant="borderless" loading={loading}>
              <div className="stat-content">
                <div className="stat-icon" style={{ background: stat.bgColor }}>
                  <span style={{ color: stat.color }}>{stat.icon}</span>
                </div>
                <div className="stat-info">
                  <div className="stat-title">{stat.title}</div>
                  <div className="stat-value">{stat.value}</div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[20, 20]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={16}>
          <Card
            title="最近核销记录"
            variant="borderless"
            className="record-card"
            extra={<a href="/appointment">查看全部</a>}
          >
            <Table
              columns={columns}
              dataSource={recentCheckins}
              rowKey="id"
              pagination={false}
              className="record-table"
              locale={{ emptyText: '暂无核销记录' }}
            />
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="系统数据" variant="borderless" className="status-card">
            <div className="status-item">
              <span>小区总数</span>
              <span className="status-value">{stats.communityCount}</span>
            </div>
            <div className="status-item">
              <span>用户总数</span>
              <span className="status-value">{stats.userCount}</span>
            </div>
            <div className="status-item">
              <span>今日核销</span>
              <span className="status-value" style={{ color: '#faad14' }}>{stats.todayCheckin}</span>
            </div>
            <div className="status-item">
              <span>累计核销</span>
              <span className="status-value" style={{ color: '#1890ff' }}>{stats.totalCheckin}</span>
            </div>
            <div className="status-item">
              <span>待审核预约</span>
              <span className="status-value" style={{ color: '#f5222d' }}>{stats.pendingAppointment}</span>
            </div>
          </Card>

          <Card title="快速操作" variant="borderless" className="quick-card" style={{ marginTop: 20 }}>
            <div className="quick-actions">
              <div className="quick-item" onClick={() => navigate('/user')}>
                <span>新增用户</span>
              </div>
              <div className="quick-item" onClick={() => navigate('/appointment')}>
                <span>访客预约</span>
              </div>
              <div className="quick-item">
                <span>导出数据</span>
              </div>
              <div className="quick-item">
                <span>批量导入</span>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
